import { useState, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import { jsPDF } from 'jspdf';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const TOOLS = [
  { id: 'merge', title: 'Merge PDFs', desc: 'Combine multiple PDF files into one.', icon: 'Combine', tint: 'bg-orange-100 text-orange-800' },
  { id: 'split', title: 'Split PDF', desc: 'Pull selected pages into a new PDF.', icon: 'Scissors', tint: 'bg-emerald-100 text-emerald-800' },
  { id: 'images', title: 'Images → PDF', desc: 'JPG / PNG into a single PDF.', icon: 'ImagePlus', tint: 'bg-sky-100 text-sky-800' },
];

export default function Tools() {
  const [active, setActive] = useState('merge');

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">PDF Tools</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">All your PDF chores, in-browser.</h1>
        <p className="text-stone-500 mt-3 max-w-xl">Everything runs locally in your browser — no uploads, no waiting.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {TOOLS.map(t => {
          const Icon = Icons[t.icon] || Icons.Wrench;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`tool-tab-${t.id}`}
              className={`text-left p-5 rounded-2xl border transition-all ${isActive ? 'bg-stone-900 text-white border-stone-900 shadow-md' : 'bg-white border-stone-200 hover:-translate-y-0.5'}`}
            >
              <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${isActive ? 'bg-white/10 text-orange-300' : t.tint}`}>
                <Icon size={18} />
              </div>
              <div className="font-display font-bold tracking-tight">{t.title}</div>
              <div className={`text-xs mt-1 ${isActive ? 'text-stone-300' : 'text-stone-500'}`}>{t.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-8">
        {active === 'merge' && <MergeTool />}
        {active === 'split' && <SplitTool />}
        {active === 'images' && <ImagesTool />}
      </div>
    </div>
  );
}

function Dropzone({ accept, multiple, files, setFiles, label, testId }) {
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    const fs = Array.from(e.dataTransfer.files).filter(f => f.type && (accept.includes(f.type) || (accept.includes('image/*') && f.type.startsWith('image/'))));
    setFiles(multiple ? [...files, ...fs] : fs.slice(0, 1));
  };
  const onPick = (e) => {
    const fs = Array.from(e.target.files);
    setFiles(multiple ? [...files, ...fs] : fs.slice(0, 1));
  };
  return (
    <div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-stone-300 rounded-2xl p-10 text-center hover:border-orange-500 hover:bg-orange-50/40 cursor-pointer transition-colors"
        data-testid={testId}
      >
        <Icons.UploadCloud size={32} className="mx-auto text-stone-400 mb-3" />
        <div className="font-display font-bold tracking-tight">{label}</div>
        <div className="text-xs text-stone-500 mt-1">Click to browse or drag & drop</div>
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(',')}
          multiple={multiple}
          onChange={onPick}
          className="hidden"
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-sm">
              <span className="flex items-center gap-2 truncate">
                <Icons.FileText size={14} className="text-stone-500" />
                <span className="truncate">{f.name}</span>
                <span className="text-stone-400 text-xs">{(f.size / 1024).toFixed(0)} KB</span>
              </span>
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-600" data-testid={`remove-file-${i}`}>
                <Icons.X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MergeTool() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const onMerge = async () => {
    if (files.length < 2) { toast.error('Add at least 2 PDFs.'); return; }
    setBusy(true);
    try {
      const merged = await PDFDocument.create();
      for (const file of files) {
        const bytes = await file.arrayBuffer();
        const pdf = await PDFDocument.load(bytes);
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        pages.forEach(p => merged.addPage(p));
      }
      const out = await merged.save();
      downloadBytes(out, 'merged.pdf');
      toast.success('Merged successfully');
    } catch (e) {
      toast.error('Could not merge — ensure files are valid PDFs.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Dropzone
        accept={['application/pdf']}
        multiple
        files={files}
        setFiles={setFiles}
        label="Drop PDF files to merge"
        testId="merge-dropzone"
      />
      <Button
        onClick={onMerge}
        disabled={busy || files.length < 2}
        className="mt-6 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 btn-clay"
        data-testid="merge-btn"
      >
        {busy ? <Icons.Loader2 className="animate-spin mr-2" size={14} /> : <Icons.Combine size={14} className="mr-2" />}
        Merge {files.length > 1 ? `(${files.length} files)` : ''}
      </Button>
    </div>
  );
}

function SplitTool() {
  const [files, setFiles] = useState([]);
  const [pages, setPages] = useState('');
  const [busy, setBusy] = useState(false);
  const onSplit = async () => {
    if (files.length === 0) { toast.error('Choose a PDF first.'); return; }
    if (!pages.trim()) { toast.error('Enter pages to keep, e.g. 1-3,5'); return; }
    setBusy(true);
    try {
      const bytes = await files[0].arrayBuffer();
      const src = await PDFDocument.load(bytes);
      const indices = parsePages(pages, src.getPageCount());
      if (indices.length === 0) { toast.error('No valid pages.'); setBusy(false); return; }
      const out = await PDFDocument.create();
      const copied = await out.copyPages(src, indices);
      copied.forEach(p => out.addPage(p));
      const data = await out.save();
      downloadBytes(data, 'split.pdf');
      toast.success(`Saved ${indices.length} page(s)`);
    } catch (e) {
      toast.error('Could not split this PDF.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Dropzone
        accept={['application/pdf']}
        multiple={false}
        files={files}
        setFiles={setFiles}
        label="Drop a PDF to split"
        testId="split-dropzone"
      />
      <div className="mt-6 max-w-md">
        <label className="text-sm font-medium text-stone-700 mb-1.5 block">Pages to extract</label>
        <Input
          value={pages}
          onChange={(e) => setPages(e.target.value)}
          placeholder="e.g. 1-3, 5, 7-9"
          className="bg-stone-50 border-stone-200 focus-visible:ring-orange-600"
          data-testid="split-pages-input"
        />
      </div>
      <Button
        onClick={onSplit}
        disabled={busy || files.length === 0}
        className="mt-6 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 btn-clay"
        data-testid="split-btn"
      >
        {busy ? <Icons.Loader2 className="animate-spin mr-2" size={14} /> : <Icons.Scissors size={14} className="mr-2" />}
        Extract pages
      </Button>
    </div>
  );
}

function ImagesTool() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const onConvert = async () => {
    if (files.length === 0) { toast.error('Add at least one image.'); return; }
    setBusy(true);
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      for (let i = 0; i < files.length; i++) {
        const dataUrl = await readAsDataURL(files[i]);
        const img = await loadImage(dataUrl);
        const ratio = Math.min((W - 48) / img.width, (H - 48) / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        if (i > 0) doc.addPage();
        const type = files[i].type.includes('png') ? 'PNG' : 'JPEG';
        doc.addImage(dataUrl, type, (W - w) / 2, (H - h) / 2, w, h);
      }
      doc.save('images.pdf');
      toast.success(`Converted ${files.length} image(s)`);
    } catch (e) {
      toast.error('Could not convert images.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Dropzone
        accept={['image/jpeg', 'image/png', 'image/webp']}
        multiple
        files={files}
        setFiles={setFiles}
        label="Drop images to convert"
        testId="images-dropzone"
      />
      <Button
        onClick={onConvert}
        disabled={busy || files.length === 0}
        className="mt-6 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 btn-clay"
        data-testid="images-convert-btn"
      >
        {busy ? <Icons.Loader2 className="animate-spin mr-2" size={14} /> : <Icons.ImagePlus size={14} className="mr-2" />}
        Convert to PDF
      </Button>
    </div>
  );
}

// helpers
function downloadBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function parsePages(spec, total) {
  const result = new Set();
  const parts = spec.split(',').map(s => s.trim()).filter(Boolean);
  for (const p of parts) {
    if (p.includes('-')) {
      const [a, b] = p.split('-').map(n => parseInt(n, 10));
      if (!isNaN(a) && !isNaN(b)) {
        for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
          if (i >= 1 && i <= total) result.add(i - 1);
        }
      }
    } else {
      const n = parseInt(p, 10);
      if (!isNaN(n) && n >= 1 && n <= total) result.add(n - 1);
    }
  }
  return Array.from(result).sort((a, b) => a - b);
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
